import { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { Box, Button, Text, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { tourStepsFor } from '../../utils/tourSteps';
import { hostName } from '../../utils/hostName';
import RoleChip from './RoleChip';

// MI-297 Phase 3: a one-time coach-mark micro-tour. Dependency-free — each step
// spotlights a real element (found via a `data-tour` selector) with a dimming
// box-shadow and an anchored popover. Steps whose target isn't visible (e.g. the
// collapsed sidebar on mobile) are skipped; if none are visible the tour ends.
//
// Which steps run comes from utils/tourSteps.js. The owner's are today's four,
// with today's copy. An invited member's ("guest") are built from their role:
// a step the role can't use is removed there rather than skipped here, so the
// counter reads x / 4 for a Viewer and x / 5 for everyone else. The skip
// below stays as a safety net for a target that isn't on screen.

// Popover box, used to decide which side of the target it fits on.
const POP_W = 280;
const POP_H = 170;

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

// First on-screen element for the selector (skip hidden/zero-size duplicates,
// e.g. an off-canvas mobile drawer copy).
function visibleRect(selector) {
  const els = document.querySelectorAll(selector);
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth) return r;
  }
  return null;
}

/**
 * `variant` is 'owner' (the default: today's tour, unchanged) or 'guest'. A
 * guest tour also takes the invitee's `role`, shown as a chip beside every
 * title so each stop reads as "this is what you can do here", and the invited
 * `workspace`, whose owner names the copy's {{name}}: first name, then the
 * workspace's name, never the email (utils/hostName.js).
 */
export default function OnboardingTour({ onFinish, variant = 'owner', role = null, workspace = null }) {
  const { t } = useTranslation('onboarding');
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  const guest = variant === 'guest';
  const steps = useMemo(() => tourStepsFor({ variant, role }), [variant, role]);
  // The owner's copy takes no values; passing none keeps it exactly as it was.
  const words = guest
    ? { name: hostName(workspace) || t('guest.fallbackName') }
    : undefined;

  const step = steps[idx];
  const isLast = idx === steps.length - 1;

  const measure = useCallback(() => visibleRect(step.selector), [step.selector]);

  // Resolve the current step's target; auto-advance past missing ones.
  useLayoutEffect(() => {
    const r = measure();
    if (!r) {
      if (isLast) onFinish();
      else setIdx((i) => i + 1);
      return;
    }
    setRect(r);
  }, [idx, measure, isLast, onFinish]);

  // Keep the spotlight aligned as the layout shifts.
  useEffect(() => {
    const update = () => { const r = measure(); if (r) setRect(r); };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [measure]);

  if (!rect) return null;

  const next = () => (isLast ? onFinish() : setIdx((i) => i + 1));

  const pad = 6;
  // Sidebar targets sit on the left, so the popover goes to their right. A
  // target near the right edge (the floating assistant) flips it to the left,
  // and one near the bottom bottom-aligns it — so it never lands off-screen or
  // on top of the very thing it's pointing at.
  const flipX = rect.right + 12 + POP_W > window.innerWidth;
  const popLeft = clamp(
    flipX ? rect.left - 12 - POP_W : rect.right + 12,
    12,
    window.innerWidth - POP_W - 12
  );
  const popTop = clamp(
    rect.top + POP_H > window.innerHeight ? rect.bottom - POP_H : rect.top,
    12,
    window.innerHeight - POP_H - 12
  );

  return (
    <Box position="fixed" inset={0} zIndex={2000}>
      {/* Click-blocker so the dimmed page can't be interacted with mid-tour. */}
      <Box position="absolute" inset={0} />

      {/* Spotlight: a hole over the target, everything else dimmed by the shadow. */}
      <Box
        position="absolute"
        top={`${rect.top - pad}px`}
        left={`${rect.left - pad}px`}
        width={`${rect.width + pad * 2}px`}
        height={`${rect.height + pad * 2}px`}
        borderRadius="10px"
        boxShadow="0 0 0 9999px rgba(0, 0, 0, 0.55)"
        pointerEvents="none"
        transition="all 0.2s ease"
      />

      {/* Anchored popover */}
      <Box
        position="absolute"
        top={`${popTop}px`}
        left={`${popLeft}px`}
        maxW="280px"
        bg="redesign.bg.popup"
        borderRadius="xl"
        boxShadow="2xl"
        p={4}
      >
        {guest ? (
          <Flex justify="space-between" align="flex-start" gap={2} mb={1}>
            <Text fontWeight="bold" fontSize="sm" letterSpacing="-0.01em" color="redesign.heading">
              {t(`${step.copy}.title`, words)}
            </Text>
            <RoleChip role={role} fontSize="11px" px="6px" py="1px" />
          </Flex>
        ) : (
          <Text fontWeight="bold" fontSize="sm" mb={1} letterSpacing="-0.01em" color="redesign.heading">
            {t(`${step.copy}.title`)}
          </Text>
        )}
        <Text fontSize="sm" color="redesign.text.muted" mb={3}>
          {t(`${step.copy}.body`, words)}
        </Text>
        <Flex justify="space-between" align="center">
          <Text fontSize="xs" color="redesign.text.muted">{idx + 1} / {steps.length}</Text>
          <Button size="xs" colorScheme="blue" onClick={next}>
            {isLast ? t('tour.done') : t('tour.next')}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
}
